import { useState, useMemo, useEffect, useCallback } from "react";

// ─── API CONFIG ───────────────────────────────────────────────────────────────
const API_URL = "https://soporte.chilebinary.cl/api";

async function apiFetch(endpoint, options = {}) {
  const token = localStorage.getItem("cb_client_token") || localStorage.getItem("cb_token");
  const res = await fetch(`${API_URL}/${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error en la API");
  return data;
}

// ─── FONTS ───────────────────────────────────────────────────────────────────
const FontLink = () => (
  <link href="https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Instrument+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
);

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const BRAND = {
  accent: "#f97316", accentHover: "#ea6c0a",
  dark: "#0a0a0a", mid: "#161616", card: "#1c1c1c",
  border: "rgba(249,115,22,0.15)", text: "#f0f0f0", muted: "#6b6b6b",
};
const CATEGORIES = ["Hardware","Software","Red / Conectividad","Correo electrónico","Impresoras","Accesos y permisos","Backup / Recuperación","Otro"];
const PRIORITY_CFG = {
  baja:    { label:"Baja",    color:"#4ade80", dot:"#4ade80" },
  media:   { label:"Media",   color:"#facc15", dot:"#facc15" },
  alta:    { label:"Alta",    color:"#fb923c", dot:"#fb923c" },
  critica: { label:"Crítica", color:"#f87171", dot:"#f87171" },
};
const STATUS_CFG = {
  abierto:     { label:"Abierto",           color:"#60a5fa", bg:"rgba(96,165,250,0.12)" },
  en_progreso: { label:"En progreso",       color:"#facc15", bg:"rgba(250,204,21,0.12)" },
  pendiente:   { label:"Pendiente cliente", color:"#c084fc", bg:"rgba(192,132,252,0.12)" },
  cerrado:     { label:"Cerrado",           color:"#4ade80", bg:"rgba(74,222,128,0.12)" },
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"});
}
function fmtShort(iso) {
  return new Date(iso).toLocaleDateString("es-CL",{day:"2-digit",month:"short"});
}
function initials(name) { return (name||"?").split(" ").map(x=>x[0]).join("").slice(0,2).toUpperCase(); }

// ─── UI ATOMS ─────────────────────────────────────────────────────────────────
const Chip = ({type,value}) => {
  const cfg = type==="status" ? STATUS_CFG[value] : PRIORITY_CFG[value];
  if (!cfg) return null;
  return (
    <span style={{background:cfg.bg||"transparent",color:cfg.color,border:`1px solid ${cfg.color}40`,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700,letterSpacing:0.4,whiteSpace:"nowrap"}}>
      {type==="priority" && <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:cfg.dot,marginRight:4,verticalAlign:"middle"}} />}
      {cfg.label}
    </span>
  );
};
const TypeTag = ({type}) => (
  <span style={{background:type==="presencial"?"rgba(74,222,128,0.1)":"rgba(96,165,250,0.1)",color:type==="presencial"?"#4ade80":"#60a5fa",border:`1px solid ${type==="presencial"?"#4ade8040":"#60a5fa40"}`,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
    {type==="presencial"?"🏢 Presencial":"💻 Remoto"}
  </span>
);
const Avatar = ({name,size=32}) => (
  <div style={{width:size,height:size,borderRadius:"50%",background:`rgba(249,115,22,0.2)`,border:`1.5px solid rgba(249,115,22,0.4)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.35,fontWeight:700,color:BRAND.accent,flexShrink:0}}>
    {initials(name)}
  </div>
);
const Btn = ({children,onClick,variant="primary",size="md",style={}}) => {
  const base = {border:"none",borderRadius:8,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",fontWeight:700,transition:"all 0.15s",letterSpacing:0.3};
  const sizes = {sm:{padding:"6px 14px",fontSize:12},md:{padding:"10px 20px",fontSize:13},lg:{padding:"14px 28px",fontSize:15}};
  const variants = {
    primary:{background:`linear-gradient(135deg,${BRAND.accent},#ea6c0a)`,color:"#fff"},
    ghost:{background:"rgba(255,255,255,0.05)",color:BRAND.text,border:`1px solid ${BRAND.border}`},
    danger:{background:"rgba(248,113,113,0.15)",color:"#f87171",border:"1px solid rgba(248,113,113,0.3)"},
    success:{background:"rgba(74,222,128,0.15)",color:"#4ade80",border:"1px solid rgba(74,222,128,0.3)"},
  };
  return <button onClick={onClick} style={{...base,...sizes[size],...variants[variant],...style}}>{children}</button>;
};
const Input = ({label,value,onChange,type="text",placeholder,required,style={}}) => (
  <div style={{display:"flex",flexDirection:"column",gap:5,...style}}>
    {label&&<label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>{label}{required&&<span style={{color:BRAND.accent}}> *</span>}</label>}
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"9px 12px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none"}} />
  </div>
);
const Select = ({label,value,onChange,options,required,style={}}) => (
  <div style={{display:"flex",flexDirection:"column",gap:5,...style}}>
    {label&&<label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>{label}{required&&<span style={{color:BRAND.accent}}> *</span>}</label>}
    <select value={value} onChange={onChange} style={{background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"9px 12px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none",cursor:"pointer"}}>
      {options.map(([v,l])=><option key={v} value={v} style={{background:BRAND.dark}}>{l}</option>)}
    </select>
  </div>
);

// ─── TOAST ────────────────────────────────────────────────────────────────────
function Toast({msg,type="success",onClose}) {
  useEffect(()=>{const t=setTimeout(onClose,4000);return()=>clearTimeout(t);},[onClose]);
  const colors = {success:{bg:"#0e2a1f",border:"rgba(74,222,128,0.35)",text:"#4ade80"},error:{bg:"#2a0e0e",border:"rgba(248,113,113,0.35)",text:"#f87171"},info:{bg:"#1c1200",border:"rgba(249,115,22,0.35)",text:BRAND.accent}};
  const c = colors[type]||colors.info;
  return (
    <div style={{position:"fixed",bottom:24,right:24,background:c.bg,border:`1px solid ${c.border}`,borderRadius:14,padding:"14px 20px",maxWidth:340,boxShadow:"0 8px 32px rgba(0,0,0,0.5)",zIndex:999,fontFamily:"'Instrument Sans',sans-serif",display:"flex",gap:10,alignItems:"center"}}>
      <div style={{color:c.text,fontWeight:700,fontSize:13,flex:1}}>{msg}</div>
      <button onClick={onClose} style={{background:"none",border:"none",color:BRAND.muted,cursor:"pointer",fontSize:18}}>×</button>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen({onLogin}) {
  const [email,setEmail] = useState("");
  const [pass,setPass] = useState("");
  const [error,setError] = useState("");
  const [loading,setLoading] = useState(false);

  const handleLogin = async () => {
    if(!email||!pass) return;
    setLoading(true); setError("");
    try {
      const data = await apiFetch("auth", {method:"POST", body:{email,password:pass}});
      localStorage.setItem("cb_token", data.token);
      localStorage.setItem("cb_user", JSON.stringify(data.user));
      onLogin(data.user);
    } catch(e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  return (
    <div style={{minHeight:"100vh",background:`radial-gradient(ellipse at 30% 40%,rgba(249,115,22,0.07) 0%,transparent 55%),linear-gradient(160deg,${BRAND.dark} 0%,#111 100%)`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Instrument Sans',sans-serif"}}>
      <FontLink/>
      <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:20,padding:"40px 36px",width:"100%",maxWidth:400,boxShadow:"0 32px 80px rgba(0,0,0,0.5)"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{width:52,height:52,background:`linear-gradient(135deg,${BRAND.accent},#ea6c0a)`,borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,color:"#fff",fontSize:20,margin:"0 auto 14px"}}>CB</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:BRAND.text}}>Panel de Técnicos</div>
          <div style={{color:BRAND.muted,fontSize:13,marginTop:4}}>CHILEBINARY HelpDesk</div>
        </div>
        <div style={{display:"grid",gap:14}}>
          <Input label="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="correo@chilebinary.cl" required />
          <Input label="Contraseña" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" required />
          {error && <div style={{color:"#f87171",fontSize:13,textAlign:"center"}}>{error}</div>}
          <Btn onClick={handleLogin} variant="primary" size="lg" style={{width:"100%",marginTop:4}}>
            {loading ? "Ingresando..." : "Ingresar →"}
          </Btn>
          </div>
        <button onClick={()=>{window.location.href="/";}} style={{width:"100%",marginTop:12,background:"none",border:"none",color:BRAND.muted,cursor:"pointer",fontSize:13,fontFamily:"'Instrument Sans',sans-serif"}}>← Volver al inicio</button>
      </div>
    </div>
  );
}
// ─── CLIENT LOGIN FORM ───────────────────────────────────────────────────────
function ClientLoginForm({onLogin}) {
  const [email,setEmail] = useState("");
  const [pass,setPass] = useState("");
  const [error,setError] = useState("");
  const [loading,setLoading] = useState(false);

  const handleLogin = async () => {
    if(!email||!pass) return;
    setLoading(true); setError("");
    const err = await onLogin(email,pass);
    if(err) setError(err);
    setLoading(false);
  };

  return (
    <div style={{background:"rgba(28,28,28,0.95)",border:`1px solid ${BRAND.border}`,borderRadius:20,padding:"32px 36px",maxWidth:560,width:"100%",boxShadow:"0 32px 80px rgba(0,0,0,0.5)",marginBottom:16}}>
      <h3 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:18,margin:"0 0 6px",fontWeight:800}}>Acceso al portal</h3>
      <p style={{color:BRAND.muted,fontSize:13,margin:"0 0 20px"}}>Ingresa con las credenciales que te proporcionó CHILEBINARY</p>
      <div style={{display:"grid",gap:14}}>
        <Input label="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="correo@empresa.com" required />
        <Input label="Contraseña" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" required />
        {error && <div style={{color:"#f87171",fontSize:13,textAlign:"center"}}>{error}</div>}
        <Btn onClick={handleLogin} variant="primary" size="lg" style={{width:"100%"}}>
          {loading?"Ingresando...":"Ingresar →"}
        </Btn>
      </div>
    </div>
  );
}
// ─── CLIENT PORTAL ───────────────────────────────────────────────────────────
function ClientPortal({onBack}) {
  const params = new URLSearchParams(window.location.search);
  const ticketParam = params.get("ticket");
  const [tab,setTab] = useState(ticketParam ? "track" : "new");
  const [clientUser, setClientUser] = useState(()=>{
  try { return JSON.parse(localStorage.getItem("cb_client")); } catch { return null; }
});
  const [trackId,setTrackId] = useState(ticketParam || "");
  const [form,setForm] = useState({client_name:"",client_email:"",client_phone:"",title:"",category:"",type:"remoto",priority:"media",description:"",uv_id:"",uv_pass:""});
  const [sent,setSent] = useState(null);
  const [loading,setLoading] = useState(false);
  const [tracked,setTracked] = useState(null);
  const [trackErr,setTrackErr] = useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  const handleSubmit = async () => {
    if(!form.title||!form.category||!form.description) return;
    if(!clientUser && (!form.client_name||!form.client_email)) return;
    setLoading(true);
    try {
      const ticketBody = clientUser ? {...form, client_name: clientUser.name, client_email: clientUser.email} : form;
      const data = await apiFetch("tickets", {method:"POST", body:ticketBody});
      setSent(data.ticket_code);
    } catch(e) { alert("Error al enviar: "+e.message); }
    finally { setLoading(false); }
  };

  const handleTrack = async () => {
    try {
      const data = await apiFetch(`tickets/0?code=${trackId.trim()}`);
      setTracked(data); setTrackErr(false);
    } catch { setTrackErr(true); setTracked(null); }
  };
  useEffect(()=>{ if(ticketParam) handleTrack(); },[]);

  const bg = {minHeight:"100vh",background:`radial-gradient(ellipse at 20% 50%,rgba(249,115,22,0.07) 0%,transparent 55%),linear-gradient(160deg,${BRAND.dark} 0%,#111 100%)`,fontFamily:"'Instrument Sans',sans-serif",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24};
  const card = {background:"rgba(28,28,28,0.95)",border:`1px solid ${BRAND.border}`,borderRadius:20,padding:"36px",maxWidth:560,width:"100%",boxShadow:"0 32px 80px rgba(0,0,0,0.5)"};
  const inputS = {background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"9px 12px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none",width:"100%",boxSizing:"border-box"};

  if(sent) return (
    <div style={bg}><FontLink/>
      <div style={{...card,textAlign:"center"}}>
        <div style={{width:72,height:72,background:"rgba(249,115,22,0.12)",border:`2px solid ${BRAND.accent}`,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",fontSize:32}}>✓</div>
        <h2 style={{color:BRAND.text,fontFamily:"'Syne',sans-serif",fontSize:24,margin:"0 0 10px"}}>¡Ticket registrado!</h2>
        <p style={{color:BRAND.muted,marginBottom:24,lineHeight:1.6}}>Nuestro equipo revisará tu solicitud y se pondrá en contacto a la brevedad. Recibirás un email de confirmación.</p>
        <div style={{background:`rgba(249,115,22,0.08)`,border:`1.5px dashed ${BRAND.accent}55`,borderRadius:12,padding:"14px 24px",marginBottom:28}}>
          <div style={{fontSize:11,color:BRAND.muted,fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:4}}>Tu número de ticket</div>
          <div style={{fontSize:32,fontWeight:800,color:BRAND.accent,letterSpacing:3,fontFamily:"'Syne',sans-serif"}}>{sent}</div>
          <div style={{fontSize:11,color:BRAND.muted,marginTop:4}}>Guárdalo para hacer seguimiento</div>
        </div>
        <Btn onClick={()=>{setSent(null);setForm({client_name:"",client_email:"",client_phone:"",title:"",category:"",type:"remoto",priority:"media",description:""});}} variant="primary">Enviar otro ticket</Btn>
      </div>
    </div>
  );
  const handleClientLogin = async (email, pass) => {
    try {
      const data = await apiFetch("client-auth", {method:"POST", body:{email, password:pass}});
      localStorage.setItem("cb_client_token", data.token);
      localStorage.setItem("cb_client", JSON.stringify(data.user));
      setClientUser(data.user);
    } catch(e) { return e.message; }
  };
  
  const handleClientLogout = () => {
    localStorage.removeItem("cb_client_token");
    localStorage.removeItem("cb_client");
    setClientUser(null);
  };
  return (
    <div style={bg}><FontLink/>
      <div style={{textAlign:"center",marginBottom:28}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:12,marginBottom:8}}>
          <div style={{width:40,height:40,background:`linear-gradient(135deg,${BRAND.accent},#ea6c0a)`,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,color:"#fff",fontSize:16}}>CB</div>
          <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:BRAND.text,letterSpacing:1}}>CHILEBINARY</span>
        </div>
        <p style={{color:BRAND.muted,fontSize:13,margin:0}}>Portal de Soporte Técnico</p>
      </div>
      {!clientUser ? (
        <ClientLoginForm onLogin={handleClientLogin} />
      ) : (
      <>
      <div style={{...card, marginBottom:16, padding:"14px 20px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
        <div>
          <div style={{color:BRAND.accent, fontWeight:700, fontSize:14}}>{clientUser.name}</div>
          <div style={{color:BRAND.muted, fontSize:12}}>{clientUser.email}</div>
        </div>
        <Btn onClick={handleClientLogout} variant="ghost" size="sm">Cerrar sesión</Btn>
      </div>
      <div style={card}>
        <div style={{display:"flex",gap:0,marginBottom:28,background:"rgba(0,0,0,0.3)",borderRadius:10,padding:4}}>
        
          {[["new","✏️  Abrir Ticket"],["track","🔍  Seguimiento"]].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} style={{flex:1,padding:"9px",border:"none",borderRadius:8,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",fontWeight:700,fontSize:13,background:tab===t?BRAND.accent:"transparent",color:tab===t?"#fff":BRAND.muted,transition:"all 0.2s"}}>{l}</button>
          ))}
        </div>

        {tab==="new" && (
          <div style={{display:"grid",gap:14}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <Input label="Nombre" value={form.client_name} onChange={e=>set("client_name",e.target.value)} placeholder="Tu nombre completo" required />
              <Input label="Teléfono" value={form.client_phone} onChange={e=>set("client_phone",e.target.value)} placeholder="+56 9 ..." />
            </div>
            <Input label="Correo electrónico" type="email" value={form.client_email} onChange={e=>set("client_email",e.target.value)} placeholder="correo@empresa.com" required />
            <Input label="Asunto del problema" value={form.title} onChange={e=>set("title",e.target.value)} placeholder="Describe brevemente el problema" required />
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
              <Select label="Categoría" value={form.category} onChange={e=>set("category",e.target.value)} options={[["","Seleccionar"],...CATEGORIES.map(c=>[c,c])]} required />
              <Select label="Tipo" value={form.type} onChange={e=>set("type",e.target.value)} options={[["remoto","💻 Remoto"],["presencial","🏢 Presencial"]]} />
              <Select label="Urgencia" value={form.priority} onChange={e=>set("priority",e.target.value)} options={[["baja","Baja"],["media","Media"],["alta","Alta"],["critica","Crítica"]]} />
            </div>
            {form.type==="remoto" && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <Input label="ID Ultraviewer" value={form.uv_id||""} onChange={e=>set("uv_id",e.target.value)} placeholder="Ej: 123456789" />
              <Input label="Clave Ultraviewer" value={form.uv_pass||""} onChange={e=>set("uv_pass",e.target.value)} placeholder="Clave de acceso" />
            </div>
          )}
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Descripción <span style={{color:BRAND.accent}}>*</span></label>
              <textarea value={form.description} onChange={e=>set("description",e.target.value)} placeholder="Describe el problema en detalle..."
                style={{...inputS,height:90,resize:"vertical"}} />
            </div>
            <Btn onClick={handleSubmit} variant="primary" size="lg" style={{width:"100%",marginTop:4}}>
              {loading?"Enviando...":"Enviar solicitud →"}
            </Btn>
          </div>
        )}

        {tab==="track" && (
          <div style={{display:"grid",gap:16}}>
            <p style={{color:BRAND.muted,fontSize:14,textAlign:"center",margin:0}}>Ingresa el número de ticket que recibiste al crear tu solicitud.</p>
            <div style={{display:"flex",gap:8}}>
              <input value={trackId} onChange={e=>setTrackId(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleTrack()} placeholder="Ej: TK-0001"
                style={{flex:1,background:BRAND.mid,border:`1.5px solid ${trackErr?"#f87171":BRAND.border}`,borderRadius:8,padding:"10px 14px",color:BRAND.text,fontSize:14,fontFamily:"'Instrument Sans',sans-serif",outline:"none"}} />
              <Btn onClick={handleTrack} variant="primary">Buscar</Btn>
            </div>
            {trackErr && <div style={{color:"#f87171",fontSize:13,textAlign:"center"}}>No se encontró ningún ticket con ese número.</div>}
            {tracked && (
              <div style={{background:"rgba(0,0,0,0.3)",border:`1px solid ${BRAND.border}`,borderRadius:14,padding:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                  <div>
                    <div style={{fontSize:11,color:BRAND.muted,fontWeight:700,letterSpacing:1}}>{tracked.ticket_code}</div>
                    <div style={{color:BRAND.text,fontWeight:700,fontSize:16,marginTop:3}}>{tracked.title}</div>
                  </div>
                  <Chip type="status" value={tracked.status} />
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                  {[["Categoría",tracked.category],["Tipo",tracked.type==="presencial"?"Presencial":"Remoto"],["Prioridad",PRIORITY_CFG[tracked.priority]?.label],["Creado",fmtDate(tracked.created_at)]].map(([k,v])=>(
                    <div key={k}><div style={{fontSize:10,color:BRAND.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.6,marginBottom:2}}>{k}</div><div style={{color:BRAND.text,fontSize:13}}>{v}</div></div>
                  ))}
                </div>
                {tracked.notes?.length>0 && (
                  <div style={{background:"rgba(249,115,22,0.05)",border:`1px solid rgba(249,115,22,0.2)`,borderRadius:8,padding:12}}>
                    <div style={{fontSize:10,color:BRAND.accent,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:6}}>Última actualización</div>
                    <div style={{color:BRAND.text,fontSize:13}}>{tracked.notes[tracked.notes.length-1].note}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      </>
      )}
      <button onClick={onBack} style={{marginTop:20,background:"none",border:"none",color:BRAND.muted,cursor:"pointer",fontSize:13}}>← Volver al inicio</button>
    </div>
  );
}

// ─── TICKET DETAIL ────────────────────────────────────────────────────────────
function TicketDetail({ticket,techs,onClose,onUpdate,onToast,onRefresh}) {
  const [t,setT] = useState(ticket);
  const [note,setNote] = useState("");
  const [notifyClient,setNotifyClient] = useState(false);
  const [saving,setSaving] = useState(false);

  const upd = async (field,value) => {
    try {
      await apiFetch(`tickets/${t.id}`, {method:"PUT", body:{[field]:value}});
      const updated = {...t,[field]:value,updated_at:new Date().toISOString()};
      // Si se asigna técnico, buscar el nombre actualizado
      if(field === "assignee_id") {
        await onRefresh();
      }
      setT(updated); onUpdate(updated);
    } catch(e) { onToast("Error al actualizar: "+e.message,"error"); }
  };

  const addNote = async () => {
    if(!note.trim()) return;
    setSaving(true);
    try {
      await apiFetch(`tickets/${t.id}/notes`, {method:"POST", body:{note, notify_client:notifyClient}});
      const newNote = {author_name:"Técnico",note,created_at:new Date().toISOString()};
      const updated = {...t,notes:[...(t.notes||[]),newNote]};
      setT(updated); onUpdate(updated);
      setNote(""); setNotifyClient(false);
      if(notifyClient) onToast("📧 Email enviado al cliente","success");
      else onToast("Nota agregada","info");
    } catch(e) { onToast("Error: "+e.message,"error"); }
    finally { setSaving(false); }
  };

  const selStyle = {background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"8px 10px",color:BRAND.text,fontSize:12,fontFamily:"'Instrument Sans',sans-serif",outline:"none",cursor:"pointer",width:"100%"};

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
      <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:20,width:"100%",maxWidth:680,maxHeight:"90vh",overflow:"auto",boxShadow:"0 40px 120px rgba(0,0,0,0.6)"}}>
        <div style={{background:`linear-gradient(135deg,#111,#1c1c1c)`,padding:"22px 24px",borderBottom:`1px solid ${BRAND.border}`,borderRadius:"20px 20px 0 0"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:11,color:BRAND.accent,fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:4}}>{t.ticket_code}</div>
              <div style={{fontSize:18,fontWeight:700,color:BRAND.text,lineHeight:1.3}}>{t.title}</div>
            </div>
            <button onClick={onClose} style={{background:"rgba(255,255,255,0.08)",border:"none",color:BRAND.muted,borderRadius:8,width:32,height:32,cursor:"pointer",fontSize:18}}>×</button>
          </div>
          <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
            <Chip type="status" value={t.status} />
            <Chip type="priority" value={t.priority} />
            <TypeTag type={t.type} />
          </div>
        </div>

        <div style={{padding:24,display:"grid",gap:18}}>
          <div style={{background:"rgba(0,0,0,0.2)",borderRadius:12,padding:14,display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
          {[["Cliente",t.client_name],["Email",t.client_email],["Teléfono",t.client_phone||"—"],["ID Ultraviewer",t.uv_id||"—"],["Clave Ultraviewer",t.uv_pass||"—"]].map(([k,v])=>(
              <div key={k}><div style={{fontSize:10,color:BRAND.muted,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:3}}>{k}</div><div style={{color:BRAND.text,fontSize:13}}>{v}</div></div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <div><div style={{fontSize:11,color:BRAND.muted,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:5}}>Estado</div>
              <select value={t.status} onChange={e=>upd("status",e.target.value)} style={selStyle}>
                {[["abierto","Abierto"],["en_progreso","En progreso"],["pendiente","Pendiente cliente"],["cerrado","Cerrado"]].map(([v,l])=><option key={v} value={v} style={{background:BRAND.dark}}>{l}</option>)}
              </select>
            </div>
            <div><div style={{fontSize:11,color:BRAND.muted,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:5}}>Prioridad</div>
              <select value={t.priority} onChange={e=>upd("priority",e.target.value)} style={selStyle}>
                {[["baja","Baja"],["media","Media"],["alta","Alta"],["critica","Crítica"]].map(([v,l])=><option key={v} value={v} style={{background:BRAND.dark}}>{l}</option>)}
              </select>
            </div>
            <div><div style={{fontSize:11,color:BRAND.muted,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:5}}>Técnico</div>
              <select value={t.assignee_id||""} onChange={e=>upd("assignee_id",e.target.value||null)} style={selStyle}>
                <option value="" style={{background:BRAND.dark}}>Sin asignar</option>
                {techs.filter(x=>x.active==1).map(x=><option key={x.id} value={x.id} style={{background:BRAND.dark}}>{x.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div style={{fontSize:11,color:BRAND.muted,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:8}}>Descripción</div>
            <div style={{background:"rgba(0,0,0,0.2)",borderRadius:10,padding:14,fontSize:13,color:BRAND.text,lineHeight:1.7}}>{t.description}</div>
          </div>

          <div>
            <div style={{fontSize:11,color:BRAND.muted,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:10}}>Notas internas</div>
            {(!t.notes||t.notes.length===0) && <div style={{fontSize:13,color:BRAND.muted,fontStyle:"italic",marginBottom:10}}>Sin notas aún.</div>}
            {(t.notes||[]).map((n,i)=>(
              <div key={i} style={{background:"rgba(250,204,21,0.05)",border:"1px solid rgba(250,204,21,0.15)",borderRadius:10,padding:"10px 14px",marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:"#facc15",marginBottom:4}}>{n.author_name} · {fmtDate(n.created_at)}</div>
                <div style={{fontSize:13,color:BRAND.text}}>{n.note}</div>
              </div>
            ))}
            <div style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap"}}>
              <input value={note} onChange={e=>setNote(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addNote()} placeholder="Agregar nota interna..."
                style={{flex:1,minWidth:200,background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"9px 12px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none"}} />
              <label style={{display:"flex",alignItems:"center",gap:6,color:BRAND.muted,fontSize:12,cursor:"pointer"}}>
                <input type="checkbox" checked={notifyClient} onChange={e=>setNotifyClient(e.target.checked)} />
                Notificar cliente
              </label>
              <Btn onClick={addNote} variant="primary" size="sm">{saving?"...":"Agregar"}</Btn>
            </div>
          </div>

          <div style={{display:"flex",gap:8,paddingTop:4,borderTop:`1px solid ${BRAND.border}`,flexWrap:"wrap"}}>
            {t.status!=="cerrado"&&<Btn onClick={()=>upd("status","cerrado")} variant="success" size="sm">✓ Marcar como resuelto</Btn>}
            <div style={{flex:1}} />
            <div style={{fontSize:11,color:BRAND.muted,alignSelf:"center"}}>Actualizado: {fmtDate(t.updated_at)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({stats,tickets,techs}) {
  if(!stats) return <div style={{padding:40,color:BRAND.muted,textAlign:"center"}}>Cargando estadísticas...</div>;

  const statusMap = Object.fromEntries((stats.totals||[]).map(x=>[x.status,parseInt(x.cnt)]));
  const byCategory = stats.byCategory||[];
  const maxCat = Math.max(...byCategory.map(x=>parseInt(x.cnt)),1);

  const StatCard = ({icon,label,value,color}) => (
    <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:"18px 20px",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:-10,right:-10,fontSize:50,opacity:0.06}}>{icon}</div>
      <div style={{fontSize:11,color:BRAND.muted,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:8}}>{label}</div>
      <div style={{fontSize:36,fontWeight:800,color,fontFamily:"'Syne',sans-serif",lineHeight:1}}>{value}</div>
    </div>
  );

  const recent = [...tickets].sort((a,b)=>new Date(b.updated_at)-new Date(a.updated_at)).slice(0,5);

  return (
    <div style={{padding:"28px",overflowY:"auto",height:"100%"}}>
      <div style={{marginBottom:24}}>
        <h2 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:22,margin:"0 0 4px",fontWeight:800}}>Dashboard</h2>
        <p style={{color:BRAND.muted,fontSize:13,margin:0}}>Resumen general del sistema de soporte</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:24}}>
        <StatCard icon="🎫" label="Total tickets" value={Object.values(statusMap).reduce((a,b)=>a+b,0)} color={BRAND.text} />
        <StatCard icon="⚠️" label="Sin asignar" value={parseInt(stats.unassigned)||0} color="#facc15" />
        <StatCard icon="🔥" label="Críticos activos" value={(stats.byPriority||[]).find(x=>x.priority==="critica")?.cnt||0} color="#f87171" />
        <StatCard icon="📂" label="Abiertos" value={statusMap.abierto||0} color="#60a5fa" />
        <StatCard icon="⚙️" label="En progreso" value={statusMap.en_progreso||0} color="#facc15" />
        <StatCard icon="✅" label="Cerrados" value={statusMap.cerrado||0} color="#4ade80" />
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
        <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:20}}>
          <div style={{fontSize:12,fontWeight:700,color:BRAND.muted,letterSpacing:0.8,textTransform:"uppercase",marginBottom:16}}>Tickets por categoría</div>
          {byCategory.map(c=>(
            <div key={c.category} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:BRAND.text,marginBottom:4}}>
                <span>{c.category.split("/")[0].trim()}</span><span style={{color:BRAND.accent,fontWeight:700}}>{c.cnt}</span>
              </div>
              <div style={{height:6,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${(parseInt(c.cnt)/maxCat)*100}%`,background:`linear-gradient(90deg,${BRAND.accent},#ea6c0a)`,borderRadius:3}} />
              </div>
            </div>
          ))}
        </div>

        <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:20}}>
          <div style={{fontSize:12,fontWeight:700,color:BRAND.muted,letterSpacing:0.8,textTransform:"uppercase",marginBottom:16}}>Carga por técnico</div>
          {(stats.byTech||[]).map(t=>(
            <div key={t.name} style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
              <Avatar name={t.name} size={30} />
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:BRAND.text,fontWeight:600,marginBottom:3}}>{t.name.split(" ")[0]}</div>
                <div style={{display:"flex",gap:6}}>
                  <span style={{fontSize:11,color:"#60a5fa"}}>{t.total-t.closed} activos</span>
                  <span style={{color:BRAND.muted}}>·</span>
                  <span style={{fontSize:11,color:"#4ade80"}}>{t.closed} cerrados</span>
                </div>
              </div>
              <div style={{fontSize:18,fontWeight:800,color:(t.total-t.closed)>2?"#fb923c":BRAND.text,fontFamily:"'Syne',sans-serif"}}>{t.total-t.closed}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:20}}>
        <div style={{fontSize:12,fontWeight:700,color:BRAND.muted,letterSpacing:0.8,textTransform:"uppercase",marginBottom:14}}>Actividad reciente</div>
        {recent.map(t=>(
          <div key={t.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:`1px solid ${BRAND.border}`}}>
            <div style={{fontSize:11,fontWeight:700,color:BRAND.muted,width:60,flexShrink:0}}>{t.ticket_code}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:BRAND.text,fontSize:13,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.title}</div>
              <div style={{color:BRAND.muted,fontSize:11}}>{t.client_name}</div>
            </div>
            <Chip type="status" value={t.status} />
            <div style={{fontSize:11,color:BRAND.muted,flexShrink:0}}>{fmtShort(t.updated_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── TICKET LIST ──────────────────────────────────────────────────────────────
function TicketList({tickets,techs,onSelect,onRefresh}) {
  const [filter,setFilter] = useState("todos");
  const [search,setSearch] = useState("");

  const filtered = useMemo(()=>{
    return tickets
      .filter(t=>filter==="todos"||t.status===filter)
      .filter(t=>!search||[t.title,t.client_name,t.ticket_code,t.category].some(f=>(f||"").toLowerCase().includes(search.toLowerCase())));
  },[tickets,filter,search]);

  const counts = {todos:tickets.length,...Object.fromEntries(Object.keys(STATUS_CFG).map(s=>[s,tickets.filter(t=>t.status===s).length]))};

  const exportCSV = () => {
    window.open(`${API_URL}/export?token=${localStorage.getItem("cb_token")}`,"_blank");
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      <div style={{padding:"20px 24px",borderBottom:`1px solid ${BRAND.border}`,flexShrink:0}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <h2 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:20,margin:0,fontWeight:800}}>Tickets</h2>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={onRefresh} variant="ghost" size="sm">↻ Actualizar</Btn>
            <Btn onClick={exportCSV} variant="ghost" size="sm">⬇ Exportar CSV</Btn>
          </div>
        </div>
        <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
          {[["todos","Todos"],["abierto","Abiertos"],["en_progreso","En progreso"],["pendiente","Pendiente"],["cerrado","Cerrados"]].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)} style={{padding:"5px 14px",borderRadius:20,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Instrument Sans',sans-serif",background:filter===v?BRAND.accent:"rgba(255,255,255,0.05)",color:filter===v?"#fff":BRAND.muted,transition:"all 0.2s"}}>
              {l} <span style={{opacity:0.7}}>({counts[v]||0})</span>
            </button>
          ))}
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Buscar por ID, cliente, categoría..."
          style={{width:"100%",background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"8px 12px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none",boxSizing:"border-box"}} />
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"12px 16px"}}>
        {filtered.length===0 && <div style={{textAlign:"center",padding:48,color:BRAND.muted}}>No hay tickets en esta categoría.</div>}
        {filtered.map(t=>(
          <div key={t.id} onClick={()=>onSelect(t)}
            style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:12,padding:"14px 16px",marginBottom:8,cursor:"pointer",borderLeft:`3px solid ${PRIORITY_CFG[t.priority]?.color||"#666"}`,transition:"all 0.15s"}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=BRAND.accent;e.currentTarget.style.background="#12223d"===BRAND.card?"#12223d":"#222";}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=BRAND.border;e.currentTarget.style.background=BRAND.card;}}>
            <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,fontWeight:700,color:BRAND.muted}}>{t.ticket_code}</span>
                  <Chip type="status" value={t.status} />
                  <Chip type="priority" value={t.priority} />
                  <TypeTag type={t.type} />
                </div>
                <div style={{color:BRAND.text,fontWeight:600,fontSize:14,marginBottom:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.title}</div>
                <div style={{color:BRAND.muted,fontSize:12}}>{t.client_name} · {t.category}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:11,color:BRAND.muted,marginBottom:5}}>{fmtShort(t.updated_at)}</div>
                {t.assignee_name
                  ? <div style={{fontSize:12,color:BRAND.accent,fontWeight:600}}>👤 {t.assignee_name.split(" ")[0]}</div>
                  : <div style={{fontSize:12,color:"#f87171",fontWeight:600}}>Sin asignar</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
// ─── CLIENTS MANAGEMENT ──────────────────────────────────────────────────────
function ClientsManagement({onToast}) {
  const [clients,setClients] = useState([]);
  const [showNew,setShowNew] = useState(false);
  const [form,setForm] = useState({company_name:"",contact_name:"",email:"",password:"",phone:""});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  const loadClients = useCallback(async()=>{
    try {
      const data = await apiFetch("clients");
      setClients(data);
    } catch(e) { onToast("Error al cargar clientes: "+e.message,"error"); }
  },[onToast]);

  useEffect(()=>{ loadClients(); },[loadClients]);

  const createClient = async () => {
    if(!form.company_name||!form.contact_name||!form.email||!form.password) return;
    try {
      await apiFetch("clients",{method:"POST",body:form});
      loadClients(); setShowNew(false);
      setForm({company_name:"",contact_name:"",email:"",password:"",phone:""});
      onToast("Cliente creado exitosamente","success");
    } catch(e) { onToast("Error: "+e.message,"error"); }
  };

  const toggleActive = async (client) => {
    try {
      await apiFetch(`clients/${client.id}`,{method:"PUT",body:{active:client.active==1?0:1}});
      loadClients();
      onToast(`Cliente ${client.active==1?"desactivado":"activado"}`,"info");
    } catch(e) { onToast("Error: "+e.message,"error"); }
  };

  const deleteClient = async (id) => {
    if(!window.confirm("¿Eliminar este cliente?")) return;
    try {
      await apiFetch(`clients/${id}`,{method:"DELETE"});
      loadClients(); onToast("Cliente eliminado","info");
    } catch(e) { onToast("Error: "+e.message,"error"); }
  };

  const inputS = {background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"9px 12px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none",width:"100%",boxSizing:"border-box"};

  return (
    <div style={{padding:"28px",overflowY:"auto",height:"100%"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
        <div>
          <h2 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:22,margin:"0 0 4px",fontWeight:800}}>Clientes</h2>
          <p style={{color:BRAND.muted,fontSize:13,margin:0}}>{clients.filter(c=>c.active==1).length} clientes activos</p>
        </div>
        <Btn onClick={()=>setShowNew(true)} variant="primary">+ Agregar cliente</Btn>
      </div>

      <div style={{display:"grid",gap:10}}>
        {clients.map(c=>(
          <div key={c.id} style={{background:BRAND.card,border:`1px solid ${c.active==1?BRAND.border:"rgba(255,255,255,0.05)"}`,borderRadius:14,padding:"16px 20px",display:"flex",alignItems:"center",gap:16,opacity:c.active==1?1:0.5}}>
            <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(249,115,22,0.15)",border:`1.5px solid rgba(249,115,22,0.3)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🏢</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                <span style={{color:BRAND.text,fontWeight:700,fontSize:15}}>{c.company_name}</span>
                {c.active!=1&&<span style={{background:"rgba(255,255,255,0.05)",color:BRAND.muted,borderRadius:20,padding:"1px 10px",fontSize:11,fontWeight:700}}>Inactivo</span>}
              </div>
              <div style={{color:BRAND.muted,fontSize:13}}>{c.contact_name} · {c.email}</div>
              {c.phone&&<div style={{color:BRAND.muted,fontSize:12}}>{c.phone}</div>}
            </div>
            <div style={{display:"flex",gap:8,flexShrink:0}}>
              <Btn onClick={()=>toggleActive(c)} variant={c.active==1?"ghost":"success"} size="sm">{c.active==1?"Desactivar":"Activar"}</Btn>
              <Btn onClick={()=>deleteClient(c.id)} variant="danger" size="sm">Eliminar</Btn>
            </div>
          </div>
        ))}
        {clients.length===0 && <div style={{textAlign:"center",padding:48,color:BRAND.muted}}>No hay clientes registrados aún.</div>}
      </div>

      {showNew && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
          <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:20,padding:32,width:"100%",maxWidth:480,boxShadow:"0 32px 80px rgba(0,0,0,0.5)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
              <h3 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:18,margin:0}}>Agregar cliente</h3>
              <button onClick={()=>setShowNew(false)} style={{background:"none",border:"none",color:BRAND.muted,cursor:"pointer",fontSize:20}}>×</button>
            </div>
            <div style={{display:"grid",gap:14}}>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Empresa *</label><input style={inputS} value={form.company_name} onChange={e=>set("company_name",e.target.value)} placeholder="Nombre de la empresa" /></div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Contacto *</label><input style={inputS} value={form.contact_name} onChange={e=>set("contact_name",e.target.value)} placeholder="Nombre del contacto" /></div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Email *</label><input style={inputS} type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="correo@empresa.com" /></div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Teléfono</label><input style={inputS} value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="+56 9 ..." /></div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Contraseña *</label><input style={inputS} type="password" value={form.password} onChange={e=>set("password",e.target.value)} placeholder="Mínimo 8 caracteres" /></div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <Btn onClick={()=>setShowNew(false)} variant="ghost" style={{flex:1}}>Cancelar</Btn>
                <Btn onClick={createClient} variant="primary" style={{flex:1}}>Crear cliente</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ─── REPORTS ─────────────────────────────────────────────────────────────────
function Reports({onToast}) {
  const [from,setFrom] = useState(new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().slice(0,10));
  const [to,setTo] = useState(new Date().toISOString().slice(0,10));
  const [data,setData] = useState(null);
  const [loading,setLoading] = useState(false);

  const load = useCallback(async()=>{
    setLoading(true);
    try {
      const d = await apiFetch(`reports?from=${from}&to=${to}`);
      setData(d);
    } catch(e) { onToast("Error al cargar reportes: "+e.message,"error"); }
    finally { setLoading(false); }
  },[from,to,onToast]);

  useEffect(()=>{ load(); },[load]);

  const maxClient = Math.max(...(data?.topClients||[]).map(x=>parseInt(x.total)),1);
  const maxDay = Math.max(...(data?.byDay||[]).map(x=>parseInt(x.total)),1);
  const maxCat = Math.max(...(data?.byCategory||[]).map(x=>parseInt(x.total)),1);

  const exportCSV = () => {
    const token = localStorage.getItem("cb_token");
    fetch(`${API_URL}/export?from=${from}&to=${to}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(r => r.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chilebinary-tickets-${from}-${to}.csv`;
      a.click();
    });
  };

  return (
    <div style={{padding:"28px",overflowY:"auto",height:"100%"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:22,margin:"0 0 4px",fontWeight:800}}>Reportes</h2>
          <p style={{color:BRAND.muted,fontSize:13,margin:0}}>Análisis de tickets por período</p>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <label style={{fontSize:11,color:BRAND.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.6}}>Desde</label>
            <input type="date" value={from} onChange={e=>setFrom(e.target.value)}
              style={{background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"7px 10px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none"}} />
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <label style={{fontSize:11,color:BRAND.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.6}}>Hasta</label>
            <input type="date" value={to} onChange={e=>setTo(e.target.value)}
              style={{background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"7px 10px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none"}} />
          </div>
          <Btn onClick={load} variant="primary" size="sm">Actualizar</Btn>
          <Btn onClick={exportCSV} variant="ghost" size="sm">⬇ CSV</Btn>
        </div>
      </div>

      {loading && <div style={{textAlign:"center",padding:48,color:BRAND.muted}}>Cargando reportes...</div>}

      {!loading && data && (
        <div style={{display:"grid",gap:20}}>
          {/* Resumen */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}}>
            {[
              ["Total tickets",data.summary.total,BRAND.text,"🎫"],
              ["Cerrados",data.summary.cerrados,"#4ade80","✅"],
              ["Abiertos",data.summary.abiertos,"#60a5fa","📂"],
              ["En progreso",data.summary.en_progreso,"#facc15","⚙️"],
              ["Críticos",data.summary.criticos,"#f87171","🔥"],
            ].map(([label,value,color,icon])=>(
              <div key={label} style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:"16px 18px",position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:-8,right:-8,fontSize:40,opacity:0.07}}>{icon}</div>
                <div style={{fontSize:10,color:BRAND.muted,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:6}}>{label}</div>
                <div style={{fontSize:32,fontWeight:800,color,fontFamily:"'Syne',sans-serif",lineHeight:1}}>{value||0}</div>
              </div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
            {/* Top clientes */}
            <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:20}}>
              <div style={{fontSize:12,fontWeight:700,color:BRAND.muted,letterSpacing:0.8,textTransform:"uppercase",marginBottom:16}}>Top clientes</div>
              {data.topClients.length===0 && <div style={{color:BRAND.muted,fontSize:13,fontStyle:"italic"}}>Sin datos en este período</div>}
              {data.topClients.map((c,i)=>(
                <div key={i} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:4}}>
                    <span style={{color:BRAND.text,fontWeight:600}}>{c.client_name}</span>
                    <span style={{color:BRAND.accent,fontWeight:800}}>{c.total} ticket{c.total>1?"s":""}</span>
                  </div>
                  <div style={{fontSize:11,color:BRAND.muted,marginBottom:4}}>{c.client_email}</div>
                  <div style={{height:6,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${(parseInt(c.total)/maxClient)*100}%`,background:`linear-gradient(90deg,${BRAND.accent},#ea6c0a)`,borderRadius:3}} />
                  </div>
                </div>
              ))}
            </div>

            {/* Por categoría */}
            <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:20}}>
              <div style={{fontSize:12,fontWeight:700,color:BRAND.muted,letterSpacing:0.8,textTransform:"uppercase",marginBottom:16}}>Por categoría</div>
              {data.byCategory.length===0 && <div style={{color:BRAND.muted,fontSize:13,fontStyle:"italic"}}>Sin datos en este período</div>}
              {data.byCategory.map((c,i)=>(
                <div key={i} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:BRAND.text,marginBottom:4}}>
                    <span>{c.category}</span><span style={{color:BRAND.accent,fontWeight:700}}>{c.total}</span>
                  </div>
                  <div style={{height:6,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${(parseInt(c.total)/maxCat)*100}%`,background:`linear-gradient(90deg,#60a5fa,#818cf8)`,borderRadius:3}} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tickets por día */}
          <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:20}}>
            <div style={{fontSize:12,fontWeight:700,color:BRAND.muted,letterSpacing:0.8,textTransform:"uppercase",marginBottom:16}}>Tickets por día</div>
            {data.byDay.length===0 && <div style={{color:BRAND.muted,fontSize:13,fontStyle:"italic"}}>Sin datos en este período</div>}
            <div style={{display:"flex",alignItems:"flex-end",gap:6,height:80}}>
              {data.byDay.map((d,i)=>(
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <div style={{fontSize:10,color:BRAND.accent,fontWeight:700}}>{d.total}</div>
                  <div style={{width:"100%",background:`linear-gradient(180deg,${BRAND.accent},#ea6c0a)`,borderRadius:"4px 4px 0 0",height:`${(parseInt(d.total)/maxDay)*60}px`,minHeight:4}} />
                  <div style={{fontSize:9,color:BRAND.muted,transform:"rotate(-45deg)",transformOrigin:"center",whiteSpace:"nowrap"}}>{d.day.slice(5)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Por técnico */}
          <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:14,padding:20}}>
            <div style={{fontSize:12,fontWeight:700,color:BRAND.muted,letterSpacing:0.8,textTransform:"uppercase",marginBottom:16}}>Rendimiento por técnico</div>
            {data.byTech.length===0 && <div style={{color:BRAND.muted,fontSize:13,fontStyle:"italic"}}>Sin datos en este período</div>}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
              {data.byTech.map((t,i)=>(
                <div key={i} style={{background:"rgba(0,0,0,0.2)",borderRadius:10,padding:14,display:"flex",alignItems:"center",gap:12}}>
                  <Avatar name={t.name} size={38} />
                  <div>
                    <div style={{color:BRAND.text,fontWeight:700,fontSize:13,marginBottom:4}}>{t.name.split(" ")[0]}</div>
                    <div style={{fontSize:12,color:"#60a5fa"}}>{t.total} asignados</div>
                    <div style={{fontSize:12,color:"#4ade80"}}>{t.closed} cerrados</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ─── TEAM MANAGEMENT ─────────────────────────────────────────────────────────
function TeamManagement({techs,onRefresh,onToast,currentUser}) {
  const [showNew,setShowNew] = useState(false);
  const [form,setForm] = useState({name:"",email:"",role:"tecnico",password:""});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  const addTech = async () => {
    if(!form.name||!form.email||!form.password) return;
    try {
      await apiFetch("technicians",{method:"POST",body:form});
      onRefresh(); setShowNew(false);
      setForm({name:"",email:"",role:"tecnico",password:""});
      onToast("Técnico agregado","success");
    } catch(e) { onToast("Error: "+e.message,"error"); }
  };

  const toggleActive = async (tech) => {
    try {
      await apiFetch(`technicians/${tech.id}`,{method:"PUT",body:{active:tech.active==1?0:1}});
      onRefresh(); onToast(`Técnico ${tech.active==1?"desactivado":"activado"}`,"info");
    } catch(e) { onToast("Error: "+e.message,"error"); }
  };

  const changeRole = async (tech,role) => {
    try {
      await apiFetch(`technicians/${tech.id}`,{method:"PUT",body:{role}});
      onRefresh();
    } catch(e) { onToast("Error: "+e.message,"error"); }
  };

  const removeTech = async (id) => {
    if(!window.confirm("¿Eliminar este técnico?")) return;
    try {
      await apiFetch(`technicians/${id}`,{method:"DELETE"});
      onRefresh(); onToast("Técnico eliminado","info");
    } catch(e) { onToast("Error: "+e.message,"error"); }
  };

  const roleCfg = {admin:{label:"Admin",color:BRAND.accent,bg:`rgba(249,115,22,0.1)`},tecnico:{label:"Técnico",color:"#60a5fa",bg:`rgba(96,165,250,0.1)`}};
  const inputS = {background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"9px 12px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none",width:"100%",boxSizing:"border-box"};

  return (
    <div style={{padding:"28px",overflowY:"auto",height:"100%"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
        <div>
          <h2 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:22,margin:"0 0 4px",fontWeight:800}}>Equipo técnico</h2>
          <p style={{color:BRAND.muted,fontSize:13,margin:0}}>{techs.filter(t=>t.active==1).length} técnicos activos</p>
        </div>
        {currentUser?.role==="admin" && <Btn onClick={()=>setShowNew(true)} variant="primary">+ Agregar técnico</Btn>}
      </div>

      <div style={{display:"grid",gap:10}}>
        {techs.map(t=>(
          <div key={t.id} style={{background:BRAND.card,border:`1px solid ${t.active==1?BRAND.border:"rgba(255,255,255,0.05)"}`,borderRadius:14,padding:"16px 20px",display:"flex",alignItems:"center",gap:16,opacity:t.active==1?1:0.5}}>
            <Avatar name={t.name} size={44} />
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                <span style={{color:BRAND.text,fontWeight:700,fontSize:15}}>{t.name}</span>
                <span style={{background:roleCfg[t.role]?.bg,color:roleCfg[t.role]?.color,border:`1px solid ${roleCfg[t.role]?.color}40`,borderRadius:20,padding:"1px 10px",fontSize:11,fontWeight:700}}>{roleCfg[t.role]?.label}</span>
                {t.active!=1&&<span style={{background:"rgba(255,255,255,0.05)",color:BRAND.muted,borderRadius:20,padding:"1px 10px",fontSize:11,fontWeight:700}}>Inactivo</span>}
              </div>
              <div style={{color:BRAND.muted,fontSize:13}}>{t.email}</div>
            </div>
            {currentUser?.role==="admin" && (
              <div style={{display:"flex",gap:8,flexShrink:0,flexWrap:"wrap"}}>
                <select value={t.role} onChange={e=>changeRole(t,e.target.value)} style={{background:BRAND.mid,border:`1px solid ${BRAND.border}`,borderRadius:7,padding:"6px 10px",color:BRAND.text,fontSize:12,fontFamily:"'Instrument Sans',sans-serif",cursor:"pointer",outline:"none"}}>
                  <option value="tecnico" style={{background:BRAND.dark}}>Técnico</option>
                  <option value="admin" style={{background:BRAND.dark}}>Admin</option>
                </select>
                <Btn onClick={()=>toggleActive(t)} variant={t.active==1?"ghost":"success"} size="sm">{t.active==1?"Desactivar":"Activar"}</Btn>
                <Btn onClick={()=>removeTech(t.id)} variant="danger" size="sm">Eliminar</Btn>
              </div>
            )}
          </div>
        ))}
      </div>

      {showNew && currentUser?.role==="admin" && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
          <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:20,padding:32,width:"100%",maxWidth:440,boxShadow:"0 32px 80px rgba(0,0,0,0.5)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
              <h3 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:18,margin:0}}>Agregar técnico</h3>
              <button onClick={()=>setShowNew(false)} style={{background:"none",border:"none",color:BRAND.muted,cursor:"pointer",fontSize:20}}>×</button>
            </div>
            <div style={{display:"grid",gap:14}}>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Nombre *</label><input style={inputS} value={form.name} onChange={e=>set("name",e.target.value)} placeholder="Nombre Apellido" /></div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Email *</label><input style={inputS} type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="correo@chilebinary.cl" /></div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Contraseña *</label><input style={inputS} type="password" value={form.password} onChange={e=>set("password",e.target.value)} placeholder="Mínimo 8 caracteres" /></div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Rol</label>
                <select style={inputS} value={form.role} onChange={e=>set("role",e.target.value)}><option value="tecnico" style={{background:BRAND.dark}}>Técnico</option><option value="admin" style={{background:BRAND.dark}}>Administrador</option></select>
              </div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <Btn onClick={()=>setShowNew(false)} variant="ghost" style={{flex:1}}>Cancelar</Btn>
                <Btn onClick={addTech} variant="primary" style={{flex:1}}>Agregar</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── NEW TICKET MODAL ─────────────────────────────────────────────────────────
function NewTicketModal({techs,onClose,onRefresh,onToast}) {
  const [form,setForm] = useState({client_name:"",client_email:"",client_phone:"",title:"",category:"",type:"remoto",priority:"media",description:"",assignee_id:""});
  const [loading,setLoading] = useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const inputS = {background:BRAND.mid,border:`1.5px solid ${BRAND.border}`,borderRadius:8,padding:"9px 12px",color:BRAND.text,fontSize:13,fontFamily:"'Instrument Sans',sans-serif",outline:"none",width:"100%",boxSizing:"border-box"};

  const submit = async () => {
    if(!form.client_name||!form.title||!form.category) return;
    setLoading(true);
    try {
      await apiFetch("tickets",{method:"POST",body:{...form,assignee_id:form.assignee_id||null}});
      onRefresh(); onClose();
      onToast("Ticket creado exitosamente","success");
    } catch(e) { onToast("Error: "+e.message,"error"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
      <div style={{background:BRAND.card,border:`1px solid ${BRAND.border}`,borderRadius:20,padding:32,width:"100%",maxWidth:560,boxShadow:"0 32px 80px rgba(0,0,0,0.5)",maxHeight:"90vh",overflow:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <h3 style={{fontFamily:"'Syne',sans-serif",color:BRAND.text,fontSize:18,margin:0}}>Nuevo ticket manual</h3>
          <button onClick={onClose} style={{background:"none",border:"none",color:BRAND.muted,cursor:"pointer",fontSize:20}}>×</button>
        </div>
        <div style={{display:"grid",gap:13}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Nombre *</label><input style={inputS} value={form.client_name} onChange={e=>set("client_name",e.target.value)} /></div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Teléfono</label><input style={inputS} value={form.client_phone} onChange={e=>set("client_phone",e.target.value)} /></div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Email</label><input style={inputS} type="email" value={form.client_email} onChange={e=>set("client_email",e.target.value)} /></div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Asunto *</label><input style={inputS} value={form.title} onChange={e=>set("title",e.target.value)} /></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Categoría *</label><select style={inputS} value={form.category} onChange={e=>set("category",e.target.value)}><option value="">—</option>{CATEGORIES.map(c=><option key={c} style={{background:BRAND.dark}}>{c}</option>)}</select></div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Tipo</label><select style={inputS} value={form.type} onChange={e=>set("type",e.target.value)}><option value="remoto" style={{background:BRAND.dark}}>Remoto</option><option value="presencial" style={{background:BRAND.dark}}>Presencial</option></select></div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Prioridad</label><select style={inputS} value={form.priority} onChange={e=>set("priority",e.target.value)}><option value="baja" style={{background:BRAND.dark}}>Baja</option><option value="media" style={{background:BRAND.dark}}>Media</option><option value="alta" style={{background:BRAND.dark}}>Alta</option><option value="critica" style={{background:BRAND.dark}}>Crítica</option></select></div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Asignar a</label><select style={inputS} value={form.assignee_id} onChange={e=>set("assignee_id",e.target.value)}><option value="" style={{background:BRAND.dark}}>Sin asignar</option>{techs.filter(t=>t.active==1).map(t=><option key={t.id} value={t.id} style={{background:BRAND.dark}}>{t.name}</option>)}</select></div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}><label style={{fontSize:11,fontWeight:700,color:BRAND.muted,letterSpacing:0.6,textTransform:"uppercase"}}>Descripción</label><textarea style={{...inputS,height:80,resize:"vertical"}} value={form.description} onChange={e=>set("description",e.target.value)} /></div>
          <div style={{display:"flex",gap:8,marginTop:4}}>
            <Btn onClick={onClose} variant="ghost" style={{flex:1}}>Cancelar</Btn>
            <Btn onClick={submit} variant="primary" style={{flex:1}}>{loading?"Creando...":"Crear ticket"}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN SHELL ──────────────────────────────────────────────────────────────
function AdminShell({user,onLogout}) {
  const [page,setPage] = useState("dashboard");
  const [tickets,setTickets] = useState([]);
  const [techs,setTechs] = useState([]);
  const [stats,setStats] = useState(null);
  const [selected,setSelected] = useState(null);
  const [showNew,setShowNew] = useState(false);
  const [toast,setToast] = useState(null);
  const [loading,setLoading] = useState(true);

  const showToast = useCallback((msg,type="info")=>setToast({msg,type}),[]);

  const loadData = useCallback(async () => {
    try {
      const [t,tc,st] = await Promise.all([
        apiFetch("tickets"),
        apiFetch("technicians"),
        apiFetch("stats"),
      ]);
      setTickets(t); setTechs(tc); setStats(st);
    } catch(e) { showToast("Error al cargar datos: "+e.message,"error"); }
    finally { setLoading(false); }
  },[showToast]);

  useEffect(()=>{ loadData(); },[loadData]);

  const updateTicket = (upd) => setTickets(prev=>prev.map(t=>t.id===upd.id?upd:t));

  const nav = [{id:"dashboard",icon:"◈",label:"Dashboard"},{id:"tickets",icon:"🎫",label:"Tickets"},{id:"team",icon:"👥",label:"Equipo"},{id:"clients",icon:"🏢",label:"Clientes"},{id:"reports",icon:"📊",label:"Reportes"}];

  return (
    <div style={{minHeight:"100vh",background:BRAND.dark,fontFamily:"'Instrument Sans',sans-serif",display:"flex"}}>
      <FontLink/>
      <div style={{width:220,background:`linear-gradient(180deg,#0d0d0d 0%,#111 100%)`,borderRight:`1px solid ${BRAND.border}`,display:"flex",flexDirection:"column",flexShrink:0}}>
        <div style={{padding:"24px 20px",borderBottom:`1px solid ${BRAND.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <div style={{width:34,height:34,background:`linear-gradient(135deg,${BRAND.accent},#ea6c0a)`,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,color:"#fff",fontSize:13}}>CB</div>
            <div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:14,color:BRAND.text,letterSpacing:0.5}}>CHILEBINARY</div>
              <div style={{fontSize:10,color:BRAND.muted}}>HelpDesk</div>
            </div>
          </div>
          <div style={{fontSize:11,color:BRAND.muted,marginTop:8}}>👤 {user.name}</div>
        </div>
        <nav style={{padding:"14px 10px",flex:1}}>
          {nav.map(n=>(
            <button key={n.id} onClick={()=>setPage(n.id)}
              style={{width:"100%",textAlign:"left",padding:"10px 14px",borderRadius:10,border:"none",cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",fontSize:14,fontWeight:page===n.id?700:500,
                background:page===n.id?`rgba(249,115,22,0.12)`:"transparent",color:page===n.id?BRAND.accent:BRAND.muted,
                display:"flex",alignItems:"center",gap:10,marginBottom:2,transition:"all 0.15s"}}>
              <span style={{fontSize:16}}>{n.icon}</span>{n.label}
              {n.id==="tickets"&&tickets.filter(t=>t.status==="abierto").length>0 && (
                <span style={{marginLeft:"auto",background:BRAND.accent,color:"#fff",borderRadius:20,padding:"0 7px",fontSize:11,fontWeight:800}}>{tickets.filter(t=>t.status==="abierto").length}</span>
              )}
            </button>
          ))}
        </nav>
        <div style={{padding:"14px 10px",borderTop:`1px solid ${BRAND.border}`}}>
          <Btn onClick={()=>setShowNew(true)} variant="primary" style={{width:"100%",textAlign:"center",marginBottom:8}}>+ Nuevo ticket</Btn>
          <Btn onClick={onLogout} variant="ghost" style={{width:"100%",textAlign:"center",fontSize:12}}>Cerrar sesión</Btn>
        </div>
        <div style={{padding:"14px 16px",borderTop:`1px solid ${BRAND.border}`}}>
          <div style={{fontSize:10,color:BRAND.muted,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:10}}>Técnicos activos</div>
          {techs.filter(t=>t.active==1).map(t=>(
            <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"#4ade80",flexShrink:0}} />
              <span style={{color:BRAND.muted,fontSize:12}}>{t.name.split(" ")[0]}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
        {loading ? (
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:BRAND.muted,fontSize:16}}>Cargando...</div>
        ) : (
          <>
            {page==="dashboard" && <Dashboard stats={stats} tickets={tickets} techs={techs} />}
            {page==="tickets" && <TicketList tickets={tickets} techs={techs} onSelect={t=>{setSelected(t);}} onRefresh={loadData} />}
            {page==="team" && <TeamManagement techs={techs} onRefresh={loadData} onToast={showToast} currentUser={user} />}
            {page==="clients" && <ClientsManagement onToast={showToast} />}
            {page==="reports" && <Reports onToast={showToast} />}
          </>
        )}
      </div>

      {selected && <TicketDetail ticket={selected} techs={techs} onClose={()=>setSelected(null)} onUpdate={upd=>{updateTicket(upd);setSelected(upd);loadData();}} onToast={showToast} onRefresh={loadData} />}      {showNew && <NewTicketModal techs={techs} onClose={()=>setShowNew(false)} onRefresh={loadData} onToast={showToast} />}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)} />}
    </div>
  );
}

// ─── LANDING ──────────────────────────────────────────────────────────────────
function Landing({onNav}) {
  return (
    <div style={{minHeight:"100vh",background:`radial-gradient(ellipse at 30% 40%,rgba(249,115,22,0.07) 0%,transparent 55%),linear-gradient(160deg,${BRAND.dark} 0%,#111 100%)`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"'Instrument Sans',sans-serif",padding:24}}>
      <FontLink/>
      <div style={{display:"inline-flex",alignItems:"center",gap:12,marginBottom:32}}>
        <div style={{width:52,height:52,background:`linear-gradient(135deg,${BRAND.accent},#ea6c0a)`,borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,color:"#fff",fontSize:20}}>CB</div>
        <div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:BRAND.text,letterSpacing:1}}>CHILEBINARY</div>
          <div style={{fontSize:12,color:BRAND.muted}}>Sistema de Soporte Técnico</div>
        </div>
      </div>

      <div style={{display:"flex",gap:0,width:"100%",maxWidth:860,borderRadius:20,overflow:"hidden",border:`1px solid ${BRAND.border}`,boxShadow:"0 32px 80px rgba(0,0,0,0.4)"}}>
        
        {/* Portal clientes - izquierda */}
        <button onClick={()=>onNav("client")}
          style={{flex:1,background:"rgba(255,255,255,0.03)",border:"none",padding:"48px 40px",cursor:"pointer",textAlign:"left",transition:"all 0.2s",fontFamily:"'Instrument Sans',sans-serif",borderRight:`1px solid ${BRAND.border}`}}
          onMouseEnter={e=>{e.currentTarget.style.background="rgba(249,115,22,0.06)";}}
          onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.03)";}}>
          <div style={{fontSize:40,marginBottom:16}}>📝</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,color:BRAND.text,fontSize:20,marginBottom:8}}>Portal de clientes</div>
          <div style={{color:BRAND.muted,fontSize:14,lineHeight:1.6,marginBottom:20}}>Abre un ticket de soporte o haz seguimiento de tu solicitud existente.</div>
          <div style={{display:"inline-flex",alignItems:"center",gap:8,color:BRAND.accent,fontWeight:700,fontSize:13}}>
            Ingresar al portal <span>→</span>
          </div>
        </button>

        {/* Separador central */}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"0 20px",background:"rgba(0,0,0,0.2)",gap:12,minWidth:60}}>
          <div style={{width:1,flex:1,background:`linear-gradient(to bottom, transparent, ${BRAND.border}, transparent)`}} />
          <div style={{width:40,height:40,borderRadius:"50%",background:`rgba(249,115,22,0.1)`,border:`1px solid ${BRAND.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>💻</div>
          <div style={{width:1,flex:1,background:`linear-gradient(to bottom, transparent, ${BRAND.border}, transparent)`}} />
        </div>

        {/* Panel técnicos - derecha */}
        <button onClick={()=>onNav("admin")}
          style={{flex:1,background:`rgba(249,115,22,0.04)`,border:"none",padding:"48px 40px",cursor:"pointer",textAlign:"left",transition:"all 0.2s",fontFamily:"'Instrument Sans',sans-serif"}}
          onMouseEnter={e=>{e.currentTarget.style.background="rgba(249,115,22,0.1)";}}
          onMouseLeave={e=>{e.currentTarget.style.background="rgba(249,115,22,0.04)";}}>
          <div style={{fontSize:40,marginBottom:16}}>⚙️</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,color:BRAND.accent,fontSize:20,marginBottom:8}}>Panel de técnicos</div>
          <div style={{color:BRAND.muted,fontSize:14,lineHeight:1.6,marginBottom:20}}>Gestión interna del equipo técnico de CHILEBINARY.</div>
          <div style={{display:"inline-flex",alignItems:"center",gap:8,color:BRAND.accent,fontWeight:700,fontSize:13}}>
            Acceder al panel <span>→</span>
          </div>
        </button>
      </div>

      <p style={{color:BRAND.muted,fontSize:12,marginTop:24,textAlign:"center"}}>
        Tu tranquilidad tecnológica es nuestra prioridad · <span style={{color:BRAND.accent}}>chilebinary.cl</span>
      </p>
    </div>
  );
}
// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  const params = new URLSearchParams(window.location.search);
  const [view,setView] = useState(params.get("ticket") ? "client" : "landing");
  const [user,setUser] = useState(()=>{
    try { return JSON.parse(localStorage.getItem("cb_user")); } catch { return null; }
  });

  const handleLogin = (u) => { setUser(u); setView("admin"); };
  const handleLogout = () => {
    localStorage.removeItem("cb_token");
    localStorage.removeItem("cb_user");
    setUser(null); setView("landing");
  };

  if(view==="client") return <ClientPortal onBack={()=>setView("landing")} />;
  if(view==="admin") {
    if(!user) return <LoginScreen onLogin={handleLogin} />;
    return <AdminShell user={user} onLogout={handleLogout} />;
  }
  return <Landing onNav={setView} />;
}